import React, { useContext, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { InputAdornment, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import InputField from '#component/form/input';
import RHFLegacyField from '#component/form/rhf/rhf-legacy-field';
import RHFTimeFields from '#component/form/rhf/rhf-time-fields';
import { ErrorsContext } from '#component/project/form/error-context';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  TitleCnt,
  StyledTitle,
  StyledAvatar,
  LabelField,
  BatteryField,
  SubGrid,
  StartDelayContainer,
  MinAlertPeriodContainer,
  AlertTimeDeviationCnt,
  AlertDeviationFieldTitle,
  AlertTextError
} from './styles';
import AlertItemActions from './alert-item-actions';
import { normalizeNumberField, createTitle, getNameKey } from './helper';
import propTypes from './prop-types';
import { convertTemperatureUnit, convertTemperature1, isUndefinedOrNull } from '#helper';

// Validated together ("At least one field is required", min/max pairs)
const LIMIT_FIELDS = ['tMin', 'tMax', 'hMin', 'hMax', 'bMin'];

/**
 * Renders MUI expansion panel with alert item fields.
 * Contains button to remove alert-item.
 * @returns {React.FC}
 */

const AlertItem = props => {
  const {
    index,
    member,
    remove,
    expanded,
    handleSetExpanded,
    item,
    forceError
  } = props;
  const measurementSystem = useSelector(s => s.account?.measurementSystem, shallowEqual);
  const [touched, setTouched] = useState({});
  const [title, setTitle] = useState(createTitle(item, 'metric', measurementSystem));
  const { errors, setErrors } = useContext(ErrorsContext);
  const { trigger } = useFormContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handlePanelClick = () => {
    if (errors.alerts[index]) {
      const untouchedErrors = [...errors.alerts];
      untouchedErrors[index] = false;
      setErrors(s => ({ ...s, alerts: untouchedErrors }));
    }
  };

  const deleteAlertItem = event => {
    event.stopPropagation();
    remove(index);
  };

  const revalidateLimits = () => {
    trigger(LIMIT_FIELDS.map(key => `${member}.${key}`));
  };

  const handleChange = (event, newValue, previousValue, name) => {
    if (!touched[name]) {
      setTouched(t => ({ ...t, [name]: true }));
    }
    revalidateLimits();
  };

  const handleBlur = (event, newValue, previousValue, name) => {
    setTitle(createTitle({ ...item, [getNameKey(name)]: newValue }, measurementSystem));
  };

  const formatTemperatureField = name => (!touched[name] ?
    value =>
      (!isUndefinedOrNull(value) && value !== '' ? `${convertTemperature1(value, 'metric', measurementSystem, 1)}` : '') :
    undefined
  );

  return (
    <StyledAccordion
      hasError={!!errors.alerts[index]}
      expanded={expanded}
      onChange={handleSetExpanded(member)}
      onClick={handlePanelClick}
    >
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index + 1}a-content`}
        id={`panel${index + 1}a-header`}
      >
        <TitleCnt>
          <StyledAvatar>{index + 1}</StyledAvatar>
          <StyledTitle>{title}</StyledTitle>
        </TitleCnt>
        <AlertItemActions deleteAlertItem={deleteAlertItem} />
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <RHFLegacyField
          name={`${member}.label`}
          component={LabelField}
          label="Label"
          variant="outlined"
          onBlur={handleBlur}
          forceError={forceError}
        />
        <RHFLegacyField
          name={`${member}.tMin`}
          component={InputField}
          label={isMobile ? 'Min. temp.' : 'Min. temperature'}
          variant="outlined"
          normalize={normalizeNumberField}
          format={formatTemperatureField(`${member}.tMin`)}
          onChange={handleChange}
          onBlur={handleBlur}
          forceError={forceError}
          InputProps={{
            endAdornment: <InputAdornment position="end">{convertTemperatureUnit(measurementSystem)}</InputAdornment>
          }}
        />
        <RHFLegacyField
          name={`${member}.tMax`}
          component={InputField}
          label={isMobile ? 'Max. temp.' : 'Max. temperature'}
          variant="outlined"
          normalize={normalizeNumberField}
          format={formatTemperatureField(`${member}.tMax`)}
          onChange={handleChange}
          onBlur={handleBlur}
          forceError={forceError}
          InputProps={{
            endAdornment: <InputAdornment position="end">{convertTemperatureUnit(measurementSystem)}</InputAdornment>
          }}
        />
        <RHFLegacyField
          name={`${member}.hMin`}
          component={InputField}
          label="Min. humidity"
          variant="outlined"
          normalize={normalizeNumberField}
          onChange={revalidateLimits}
          onBlur={handleBlur}
          forceError={forceError}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }}
        />
        <RHFLegacyField
          name={`${member}.hMax`}
          component={InputField}
          label="Max. humidity"
          variant="outlined"
          normalize={normalizeNumberField}
          onChange={revalidateLimits}
          onBlur={handleBlur}
          forceError={forceError}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }}
        />
        <RHFLegacyField
          name={`${member}.bMin`}
          component={BatteryField}
          label="Min. battery"
          variant="outlined"
          normalize={normalizeNumberField}
          onChange={revalidateLimits}
          onBlur={handleBlur}
          forceError={forceError}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }}
        />
        <SubGrid>
          <RHFTimeFields
            name={`${member}.startDelay`}
            label="Start delay"
            Container={StartDelayContainer}
            TimeDeviationCnt={AlertTimeDeviationCnt}
            DeviationFieldTitle={AlertDeviationFieldTitle}
            TextError={AlertTextError}
            {...(!isMobile && {
              hint: {
                tooltip: 'A delay at the beginning of shipment or application'
              }
            })}
          />
          <RHFTimeFields
            name={`${member}.minAlertPeriod`}
            label="Alert delay"
            Container={MinAlertPeriodContainer}
            TimeDeviationCnt={AlertTimeDeviationCnt}
            DeviationFieldTitle={AlertDeviationFieldTitle}
            TextError={AlertTextError}
            {...(!isMobile && {
              hint: {
                tooltip: 'A delay after crossing a threshold (before an alert will be triggered)'
              }
            })}
          />
        </SubGrid>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

AlertItem.propTypes = propTypes;

export default AlertItem;
